import type { FC, FormEvent, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'

import { useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { ArrowRight, LoaderCircle } from 'lucide-react'
import { Seo } from '@/components/generals/seo'
import { Button } from '@/components/ui/button'
import { api, errorMessage } from '@/lib/api-client'
import { redirectIfSignedIn } from '@/lib/server/ssr'
import AuthLayout from '@/components/auth/auth-layout'
import Field from '@/components/auth/field'

interface LoginProps {
    firstUser: boolean
    signupsOpen: boolean
}

const safeNext = (value: unknown): string => {
    return typeof value === 'string' && value.startsWith('/') && !value.startsWith('//') ? value : '/'
}

const Login: FC<LoginProps> = ({ firstUser }): ReactNode => {
    const router = useRouter()
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [error, setError] = useState<string | null>(null)
    const [loading, setLoading] = useState(false)

    const submit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        setError(null)
        setLoading(true)

        try {
            await api('/api/auth/login', { method: 'POST', body: { email, password } })
            await router.replace(safeNext(router.query.next))
        }
        catch (caught) {
            setError(errorMessage(caught))
            setLoading(false)
        }
    }

    return (
        <>
            <Seo title='Sign in' />

            <AuthLayout
                title='Welcome back'
                subtitle='Sign in to analyze websites and continue your rebuilds.'
                footer={
                    firstUser ? (
                        <span>No account yet? <Link href='/signup' className='font-medium text-violet-300 hover:text-white'>Create the owner account</Link></span>
                    ) : (
                        <span>Have an invite code? <Link href='/signup' className='font-medium text-violet-300 hover:text-white'>Create an account</Link></span>
                    )
                }
            >
                <form onSubmit={submit} className='space-y-5' noValidate>
                    <Field
                        id='email'
                        label='Email'
                        type='email'
                        autoComplete='email'
                        placeholder='you@company.com'
                        value={email}
                        onChange={event => setEmail(event.target.value)}
                        required
                    />
                    <Field
                        id='password'
                        label='Password'
                        type='password'
                        autoComplete='current-password'
                        placeholder='Your password'
                        value={password}
                        onChange={event => setPassword(event.target.value)}
                        required
                    />

                    {error && (
                        <p role='alert' className='rounded-xl border border-rose-500/30 bg-rose-500/10 px-3.5 py-2.5 text-sm text-rose-200'>
                            {error}
                        </p>
                    )}

                    <Button
                        type='submit'
                        disabled={loading || !email || !password}
                        className='h-11 w-full rounded-xl bg-brand-gradient text-[15px] font-semibold text-white shadow-[0_10px_30px_-10px_rgba(139,92,246,0.8)] hover:brightness-110'
                    >
                        {loading ? <LoaderCircle className='size-4 animate-spin' /> : <ArrowRight className='size-4' />}
                        {loading ? 'Signing in' : 'Sign in'}
                    </Button>
                </form>
            </AuthLayout>
        </>
    )
}

export const getServerSideProps: GetServerSideProps<LoginProps> = async context => redirectIfSignedIn(context)

export default Login
